"use client";

import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { WorldCupGroup } from "@/lib/world-cup-data";
import { WorldCupGroupCard } from "@/components/site/world-cup/world-cup-group-card";
import { cn } from "@/lib/utils";

const groupsPerPage = 4;

export function WorldCupGroupGrid({ groups }: { groups: WorldCupGroup[] }) {
  const [pageIndex, setPageIndex] = useState(0);
  const pageCount = Math.max(1, Math.ceil(groups.length / groupsPerPage));
  const activePageIndex = Math.min(pageIndex, pageCount - 1);
  const visibleGroups = useMemo(
    () => groups.slice(activePageIndex * groupsPerPage, activePageIndex * groupsPerPage + groupsPerPage),
    [groups, activePageIndex],
  );

  function previousPage() {
    setPageIndex(Math.max(0, activePageIndex - 1));
  }

  function nextPage() {
    setPageIndex(Math.min(pageCount - 1, activePageIndex + 1));
  }

  return (
    <section>
      <div className="mb-6">
        <p className="mb-1 text-xs font-bold uppercase tracking-widest text-[#A4243B]">Csoportok</p>
        <h2 className="text-3xl font-black text-[#111111]">VB csoportbeosztás</h2>
        <div className="mt-4 h-px bg-border" />
      </div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <p className="text-xs font-bold uppercase tracking-widest text-[#6B6256]">Összes csoport</p>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {Array.from({ length: pageCount }, (_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => setPageIndex(index)}
                className={cn(
                  "h-1.5 rounded-full transition-all",
                  index === activePageIndex ? "w-6 bg-[#A4243B]" : "w-1.5 bg-border hover:bg-[#6B6256]",
                )}
                aria-label={`${index + 1}. csoport oldal`}
                aria-current={index === activePageIndex ? "page" : undefined}
              />
            ))}
          </div>
          <span className="text-sm font-semibold tabular-nums text-[#111111]">
            {activePageIndex + 1} / {pageCount}
          </span>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={previousPage}
              disabled={activePageIndex === 0}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-[#111111] transition-colors hover:bg-[#F6F1E3] disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Előző csoportok"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={nextPage}
              disabled={activePageIndex === pageCount - 1}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-white text-[#111111] transition-colors hover:bg-[#F6F1E3] disabled:cursor-not-allowed disabled:opacity-30"
              aria-label="Következő csoportok"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2.5 sm:gap-3 md:grid-cols-2 md:gap-4 2xl:grid-cols-4">
        {visibleGroups.map((group) => (
          <WorldCupGroupCard key={group.id} group={group} />
        ))}
      </div>
    </section>
  );
}
